/**
 * Cart preorder backfill module.
 *
 * Purpose:
 * - Reconcile cart line preorder metadata (see PREORDER_FLAG_KEY and related keys)
 *   by querying CONFIGURATOR_AVAILABILITY_PROXY_PATH.
 * - Keep network operations bounded via PREORDER_PROXY_TIMEOUT_MS for proxy lookups
 *   and CART_CHANGE_TIMEOUT_MS for /cart/change.js updates.
 *
 * Notes:
 * - backfillPromise is internal lifecycle state for this module's background run;
 *   it is not intended for external awaiting.
 */
const PREORDER_FLAG_KEY = "_x3_configurator_preorder";
const PREORDER_TITLE_KEY = "_x3_configurator_preorder_title";
const PREORDER_CHECKBOX_TEXT_KEY = "_x3_configurator_preorder_checkbox_text";
const PREORDER_LABEL_KEY = "_x3_configurator_preorder_label";
const PREORDER_INFO_KEY = "_x3_configurator_preorder_info";
const CONFIGURATOR_AVAILABILITY_PROXY_PATH = "/apps/jbc/x3-configurator/availability";
const PREORDER_PROXY_TIMEOUT_MS = 4000;
const CART_CHANGE_TIMEOUT_MS = 1500;

let backfillPromise = null;

function parseJson(value, fallback) {
  if (!value) return fallback;

  try {
    return JSON.parse(value);
  } catch {
    return fallback;
  }
}

function normalizeLineProperties(value) {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {};
  }

  return value;
}

function addPreorderBadge(row) {
  const titleRow = row.querySelector(".cart-items__title-row");
  if (!titleRow) return;
  if (titleRow.querySelector(".cart-items__preorder-badge")) return;

  const badge = document.createElement("span");
  badge.className = "cart-items__preorder-badge";
  badge.textContent = row.dataset.preorderBadgeLabel || "Pre-Order";
  titleRow.appendChild(badge);
}

function removePreorderBadge(row) {
  const titleRow = row.querySelector(".cart-items__title-row");
  if (!titleRow) return;
  const badge = titleRow.querySelector(".cart-items__preorder-badge");
  if (badge) {
    badge.remove();
  }
}

function removePreorderProperties(properties) {
  const next = { ...properties };
  delete next[PREORDER_FLAG_KEY];
  delete next[PREORDER_TITLE_KEY];
  delete next[PREORDER_CHECKBOX_TEXT_KEY];
  delete next[PREORDER_LABEL_KEY];
  delete next[PREORDER_INFO_KEY];
  return next;
}

function getPreorderPayloadFromProperties(properties) {
  return {
    [PREORDER_FLAG_KEY]: properties[PREORDER_FLAG_KEY] || null,
    [PREORDER_TITLE_KEY]: properties[PREORDER_TITLE_KEY] || null,
    [PREORDER_CHECKBOX_TEXT_KEY]: properties[PREORDER_CHECKBOX_TEXT_KEY] || null,
    [PREORDER_LABEL_KEY]: properties[PREORDER_LABEL_KEY] || null,
    [PREORDER_INFO_KEY]: properties[PREORDER_INFO_KEY] || null,
  };
}

function isEqualPreorderPayload(a, b) {
  return (
    a[PREORDER_FLAG_KEY] === b[PREORDER_FLAG_KEY] &&
    a[PREORDER_TITLE_KEY] === b[PREORDER_TITLE_KEY] &&
    a[PREORDER_CHECKBOX_TEXT_KEY] === b[PREORDER_CHECKBOX_TEXT_KEY] &&
    a[PREORDER_LABEL_KEY] === b[PREORDER_LABEL_KEY] &&
    a[PREORDER_INFO_KEY] === b[PREORDER_INFO_KEY]
  );
}

async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timeoutId = window.setTimeout(() => {
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, {
      ...options,
      signal: controller.signal,
    });
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function fetchPreorderStatus(rows) {
  const lines = rows
    .map((row) => ({
      lineKey: row.dataset.key || "",
      variantId: row.dataset.variantId || "",
      properties: normalizeLineProperties(parseJson(row.dataset.currentProperties, {})),
    }))
    .filter((line) => line.lineKey && line.variantId);

  if (lines.length === 0) {
    return new Map();
  }

  let response;

  try {
    response = await fetchWithTimeout(
      CONFIGURATOR_AVAILABILITY_PROXY_PATH,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ lines }),
      },
      PREORDER_PROXY_TIMEOUT_MS
    );
  } catch (error) {
    throw new Error(
      `[preorder-backfill] Failed to fetch preorder status: ${error instanceof Error ? error.message : String(error)}`
    );
  }

  if (!response.ok) {
    throw new Error(`[preorder-backfill] Preorder status lookup failed with status ${response.status}`);
  }

  const data = await response.json();
  const results = Array.isArray(data?.results) ? data.results : [];
  const statusByLineKey = new Map();

  results.forEach((result) => {
    const lineKey = result?.lineKey;
    if (!lineKey) return;
    statusByLineKey.set(lineKey, result);
  });

  return statusByLineKey;
}

async function backfillPreorderRows() {
  const rows = Array.from(document.querySelectorAll(".cart-items__table-row[data-key]"));
  const preorderRows = rows.filter((row) => row.dataset.preorderAllowed !== "false");
  const statusByLineKey = await fetchPreorderStatus(preorderRows);

  for (const row of preorderRows) {
    const key = row.dataset.key;
    if (!key) continue;

    const quantity = Number.parseInt(row.dataset.quantity || "1", 10) || 1;
    const status = statusByLineKey.get(key);
    if (!status) {
      continue;
    }

    const currentProperties = normalizeLineProperties(parseJson(row.dataset.currentProperties, {}));
    const currentPreorderPayload = getPreorderPayloadFromProperties(currentProperties);

    let nextProperties = currentProperties;
    let nextPreorderPayload = currentPreorderPayload;

    if (status?.preorderActive) {
      const entries = Array.isArray(status.entries) ? status.entries : [];
      const labels = entries.map((entry) => String(entry?.label || "")).filter(Boolean);
      const infos = entries.map((entry) => String(entry?.info || ""));

      nextPreorderPayload = {
        [PREORDER_FLAG_KEY]: "true",
        [PREORDER_TITLE_KEY]: status.title || row.dataset.preorderTitle || "Preorder details",
        [PREORDER_CHECKBOX_TEXT_KEY]:
          status.checkboxText ||
          row.dataset.preorderCheckboxText ||
          "I understand these items are preorders and will ship when available.",
        [PREORDER_LABEL_KEY]: JSON.stringify(labels),
        [PREORDER_INFO_KEY]: JSON.stringify(infos),
      };

      nextProperties = {
        ...currentProperties,
        ...nextPreorderPayload,
      };
    } else {
      nextProperties = removePreorderProperties(currentProperties);
      nextPreorderPayload = getPreorderPayloadFromProperties(nextProperties);
    }

    if (isEqualPreorderPayload(currentPreorderPayload, nextPreorderPayload)) {
      if (status?.preorderActive) {
        addPreorderBadge(row);
        row.dataset.preorderMarked = "true";
      } else {
        removePreorderBadge(row);
        row.dataset.preorderMarked = "false";
      }
      continue;
    }

    let response;

    try {
      response = await fetchWithTimeout(
        "/cart/change.js",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            id: key,
            quantity,
            properties: nextProperties,
          }),
        },
        CART_CHANGE_TIMEOUT_MS
      );
    } catch (error) {
      console.warn("[preorder-backfill] Cart change request failed", {
        key,
        error: error instanceof Error ? error.message : String(error),
      });
      continue;
    }

    if (!response.ok) {
      console.warn("[preorder-backfill] Cart change request returned non-OK response", {
        key,
        status: response.status,
      });
      continue;
    }

    row.dataset.currentProperties = JSON.stringify(nextProperties);

    if (status?.preorderActive) {
      row.dataset.preorderMarked = "true";
      addPreorderBadge(row);
    } else {
      row.dataset.preorderMarked = "false";
      removePreorderBadge(row);
    }
  }
}

function initPreorderBackfill() {
  const hasRows = document.querySelector(".cart-items__table-row[data-key][data-variant-id]");
  if (!hasRows) return;

  backfillPromise = backfillPreorderRows();
  backfillPromise.catch((error) => {
    console.error("[preorder-backfill] Backfill failed", error);
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initPreorderBackfill, { once: true });
} else {
  initPreorderBackfill();
}
